<!-- MathJax Mixin

     - Load MathJax on demand when there's MathML in a page.
     - Typeset pages when ready.
     - Clear pages from typesetter when they unload.

     See https://docs.mathjax.org/en/latest/web/typeset.html#typeset-async
  -->

<script>
import * as R from 'ramda'

/**
 * How long to wait between queuing a todo and typesetting it, assuming it
 * hasn't left the queue in the meantime.
 */
const DELAY_MS = 500

/**
 * Prevent more than one caller from trying to lazy-load the MathJax script.
 */
let bootstrapping = false

/**
 * Queue of {node, timestamp}. The node indicates what to typeset, and the
 * timestamp indicates when it was added to the queue.
 */
let todos = []

/**
 * Track which nodes have been typeset, so we can clear them when the page
 * unloads.
 */
const done = new WeakSet()

/**
 * Start of the promise chain.
 */
let promise = Promise.resolve()

function typeset() {
  if (typeof window === 'undefined') {
    // Don't attempt to typeset except in the browser.
    return
  }

  // Lazy load MathJax, since most books don't contain MathML.
  if (!window.MathJax?.typesetPromise) {
    if (!bootstrapping) {
      console.debug('MathJaxMixin bootstrapping')
      bootstrapping = true
      window.MathJax = R.mergeDeepRight(window.MathJax, {
        startup: {pageReady: typeset},
      })
      const script = document.createElement('script')
      script.id = 'MathJax-script'
      script.async = true
      script.src =
        window.MathJaxSrc ||
        'https://cdn.jsdelivr.net/npm/mathjax@3/es5/mml-svg.js'
      document.head.appendChild(script)
    }
    console.debug('MathJaxMixin waiting for bootstrap')
    return
  }

  console.debug('MathJaxMixin checking todos')

  if (todos[0]) {
    const snooze = todos[0].timestamp + DELAY_MS - Date.now()
    if (snooze > 0) {
      promise = promise
        .then(() => {
          // Double check the todo and its timestamp, because the queue might
          // have changed.
          if (todos[0]) {
            const snooze = todos[0].timestamp + DELAY_MS - Date.now()
            if (snooze > 0) {
              console.debug('MathJaxMixin snoozing', snooze)
              return new Promise(resolve => setTimeout(resolve, snooze))
            }
          }
        })
        .then(() => {
          // When done snoozing, call back to typeset() to check the queue again.
          // In the normal case, this will land in the "else" condition below.
          // N.B. Do NOT "return typeset()" here, because that makes a loop.
          typeset()
        })
    } else {
      console.debug('MathJaxMixin promising', todos[0])
      promise = promise
        .then(() => {
          const todo = todos.shift()
          console.debug('MathJaxMixin typesetting', todo)
          if (todo) {
            done.add(todo.node)
            return window.MathJax.typesetPromise([todo.node]).catch(e => {
              console.error('MathJaxMixin typeset failed', e)
            })
          }
          console.debug('MathJaxMixin raced to empty')
        })
        .then(() => {
          // When done typesetting, call back to typeset() to check the queue
          // again. This will either snooze or typeset, or fall through if
          // there's nothing more in the queue. N.B. Do NOT "return typeset()"
          // here, because that makes a loop.
          typeset()
        })
    }
  } else {
    console.debug('MathJaxMixin nothing to do')
  }

  // Return the promise for window.MathJax.startup.ready which wants it.
  return promise
}

export default {
  methods: {
    $mathjaxTypeset(nodes) {
      // If there are no outstanding todos, we'll need to jumpstart the
      // typesetter. Once it's running, it will chain promises through the
      // todos.
      const needsJumpstart = todos.length === 0

      // Add these nodes to the list of todos, making sure that we're not
      // repeating ourselves.
      const newTodos = nodes.map(node => ({node, timestamp: Date.now()}))
      todos = R.uniqWith((a, b) => a.node === b.node, [...todos, ...newTodos])

      if (todos.length && needsJumpstart) {
        typeset()
      }
    },

    $mathjaxClear(nodes) {
      // Remove anything that was never actually typeset from the list of todos.
      const [dropTodos, keepTodos] = R.partition(
        todo => nodes.includes(todo.node),
        todos,
      )
      if (dropTodos.length) {
        console.debug('MathJaxMixin dropping', dropTodos)
        todos = keepTodos
      }

      // Clear the ones that were already typeset.
      const clearNodes = [...nodes].filter(node => done.has(node))
      if (clearNodes.length) {
        console.debug('MathJaxMixin clearing', clearNodes)
        window.MathJax.typesetClear(clearNodes)
      }
    },
  },
}
</script>
